import { searchHeatsWithProcessId } from "@/pages/meltshop/heats/repositories/heatsRepository";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	ArrowBackRounded,
	ArrowForwardRounded,
	SkipNext,
} from "@mui/icons-material";
import {
	Box,
	CircularProgress,
	Grid2 as Grid,
	IconButton,
	InputAdornment,
	Paper,
	TextField,
	ThemeProvider,
	createTheme,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { debounce } from "lodash-es";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { HeatData } from "../../models/HeatReportModels";

const navigatorTheme = createTheme({
	components: {
		MuiTextField: {
			styleOverrides: {
				root: {
					"& .MuiOutlinedInput-root": {
						height: "60px !important",
					},
					"& .MuiInputLabel-outlined": {
						fontSize: "24px !important",
						transform: "translate(14px, 18px) scale(1) !important",
					},
					"& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
						transform: "translate(14px, -9px) scale(0.5) !important",
						backgroundColor: "white",
						padding: "10 4px !important",
					},
					"& .MuiOutlinedInput-input": {
						fontSize: "24px !important",
						padding: "18px 14px !important",
					},
				},
			},
		},
	},
});

interface HeatProps {
	Name: string;
	Id: number;
}

interface Props {
	iconButton?: ReactElement;
	showNext?: boolean;
	showPrevious?: boolean;
	isProcessLoading?: boolean;
	value: HeatData;
	secondValue?: string;
	elevation?: number;

	onButtonClick?: () => void;
	onNext?: (heatId: number | null) => void;
	onPrevious?: (heatId: number | null) => void;
	onLast?: () => void;
	onChange?: (value: number) => void;
	onBlur?: () => void;
	onFocus?: () => void;
}

export const HeatNavigatorWithSearch = ({
	iconButton,
	showNext,
	showPrevious,
	value,
	isProcessLoading = false,
	secondValue,
	elevation = 0,
	onButtonClick,
	onNext,
	onPrevious,
	onLast,
	onChange,
	onBlur,
	onFocus,
}: Props) => {
	const navigate = useNavigate();
	const [error, setError] = useState<string>("");
	const [loading, setLoading] = useState(false);
	const [heats, setHeats] = useState<HeatProps[]>([]);
	const [selectedHeat, setSelectedHeat] = useState<HeatProps | null>({
		Name: value.HeatName,
		Id: value.HeatId!,
	});
	const [isFocused, setFocused] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");

	const currentSearch = useRef(searchTerm);

	currentSearch.current = searchTerm;

	const debouncedSearch = useCallback(
		debounce(async (term: string) => {
			setLoading(true);
			try {
				if (term.length >= 2) {
					const resp = await searchHeatsWithProcessId(term);
					if (resp.ok) {
						const data = resp.data.tables[0].rows;
						if (currentSearch.current === term) setHeats(data);
					} else {
						setError(resp.message);
					}
				}
				if (term.length === 0) {
					const resp = await searchHeatsWithProcessId(term);
					if (resp.ok) {
						if (currentSearch.current === term)
							setHeats(resp.data.tables[0].rows);
					} else {
						setError(resp.message);
					}
				}
			} catch (error) {
				setError("An error occurred while searching");
				setHeats([]);
			} finally {
				setLoading(false);
			}
		}, 1500),
		[],
	);
	const handleSelectionChange = (event: any, newValue: HeatProps | null) => {
		if (newValue) {
			if (newValue.Id !== value.HeatId) {
				setSelectedHeat(newValue);
				if (onChange) {
					onChange(newValue.Id);
				}

				setTimeout(() => {
					document.getElementById("dummyDiv")?.focus();
				}, 500);
			}
		} else {
			setSelectedHeat(null);
		}
	};

	useEffect(() => {
		debouncedSearch(searchTerm);
		return () => {
			debouncedSearch.cancel();
		};
	}, [searchTerm, debouncedSearch]);

	const options = useMemo(() => {
		if (selectedHeat && !heats.some((heat) => heat.Id === selectedHeat.Id)) {
			return [selectedHeat, ...heats];
		}
		return heats;
	}, [heats, selectedHeat]);

	useEffect(() => {
		if (isProcessLoading) {
			// When externally loading, clear the selection to show empty input
			setSelectedHeat(null);
			setSearchTerm("");
			return;
		}
		if (!isFocused && !loading) {
			if (searchTerm !== value.HeatName) {
				setSelectedHeat({
					Name: value.HeatName,
					Id: value.HeatId!,
				});
			}
		}
	}, [isFocused, searchTerm, value, loading, isProcessLoading]);

	return (
		<ThemeProvider theme={navigatorTheme}>
			<>
				<Paper elevation={elevation}>
					<Box padding={1}>
						<Grid
							container
							direction={"row"}
							alignItems={"center"}
							justifyContent={"center"}
							spacing={1}
						>
							<Grid>
								<IconButton
									aria-label="previous"
									color={"primary"}
									disabled={!showPrevious}
									onClick={() => {
										onPrevious?.(value.PreviousHeatId);
									}}
								>
									<ArrowBackRounded style={{ cursor: "pointer" }} />
								</IconButton>
							</Grid>
							<Grid style={{ minWidth: "129px" }}>
								<Autocomplete<HeatProps>
									autoHighlight
									blurOnSelect
									clearOnEscape
									selectOnFocus
									popupIcon={null}
									options={options}
									getOptionLabel={(option) => option?.Name || "Unlinked"}
									isOptionEqualToValue={(option, value) =>
										option.Id === value.Id
									}
									value={selectedHeat}
									onChange={handleSelectionChange}
									inputValue={searchTerm}
									onInputChange={(event, newInputValue) => {
										setSearchTerm(newInputValue);
									}}
									onFocus={(ev) => {
										setFocused(true);
										onFocus && onFocus();
									}}
									onBlur={(ev: any) => {
										setFocused(false);
										onBlur && onBlur();
									}}
									loading={loading || isProcessLoading}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Heat name"
											variant="outlined"
											size="medium"
											slotProps={{
												inputLabel: {
													...params.InputLabelProps,
													shrink: true,
												},
												input: {
													...params.InputProps,
													style: { padding: "0 8px 0px 8px" },
													endAdornment: (
														<InputAdornment position="end">
															{isProcessLoading || loading ? (
																<CircularProgress size="1rem" />
															) : (
																iconButton && (
																	<IconButton
																		size={"small"}
																		title={value.HeatId ? "Unlink" : "Link"}
																		onClick={onButtonClick}
																		style={{
																			color: value.HeatId ? "red" : "#3f51b5",
																		}}
																	>
																		{iconButton}
																	</IconButton>
																)
															)}
														</InputAdornment>
													),
												},
											}}
										/>
									)}
								/>
							</Grid>
							<Grid>
								<IconButton
									aria-label="next"
									color={"primary"}
									disabled={!showNext}
									onClick={() => {
										onNext?.(value.NextHeatId);
									}}
								>
									<ArrowForwardRounded style={{ cursor: "pointer" }} />
								</IconButton>
							</Grid>
							<Grid>
								<IconButton
									color={"primary"}
									onClick={onLast}
									disabled={!showNext}
									title="Last process"
								>
									<SkipNext style={{ cursor: "pointer" }} />
								</IconButton>
							</Grid>
						</Grid>
					</Box>
				</Paper>
				<ErrorModal error={error} onHide={() => setError("")} />
			</>
		</ThemeProvider>
	);
};
